/**
 * Recovery from a deploy that happened while the app was open.
 *
 * Found live on 2026-08-22: a tab open across a deploy showed "Failed to fetch
 * dynamically imported module: /assets/PropertyDetailScreen-<hash>.js" on the
 * first navigation and could not get out of it. Not a build problem — the
 * ordinary consequence of three correct decisions interacting:
 *
 *   1. Routes are code-split (docs/03 §10), so a screen loads on demand.
 *   2. Asset filenames are content-hashed, so a new build has new names and the
 *      old ones stop existing.
 *   3. The service worker registers with `autoUpdate` (vite.config.ts), which
 *      means `skipWaiting` + `clientsClaim`: the new worker activates at once
 *      and claims tabs already open.
 *
 * So the running tab holds a module graph naming files that the new worker has
 * dropped from its precache and the server no longer has. Its next lazy import
 * 404s. Reverting (3) to 'prompt' would trade this for a worse bug — users
 * pinned to a stale build until every tab closes — so the fix belongs here: a
 * failed chunk load means "your tab is out of date", and the answer is to
 * reload it.
 *
 * ONE reload, then a cool-off. A genuinely missing asset — a broken deploy —
 * must surface as an error the user can report, not an infinite refresh loop
 * that looks like the app hanging.
 *
 * The cool-off is a TIMESTAMP rather than a flag cleared when the app mounts,
 * because mounting proves nothing about the chunk that failed: the first draft
 * cleared the guard on mount, the shell reloaded fine, the same lazy import
 * failed again, and the tab reloaded forever. An e2e test now holds that shut.
 */

const RELOAD_STAMP = 'propos:stale-build-reload';

/**
 * How long after a recovery reload to stop trying and show the error instead.
 * Long enough that a still-broken chunk cannot loop; short enough that a second
 * deploy later in the same session gets its own single reload.
 */
const COOL_OFF_MS = 30_000;

/**
 * Does this error mean "the file I asked for is not there any more"?
 *
 * Matched on message text because there is no typed error for it: Vite, the
 * browser and Workbox each phrase it differently, and none of them subclass
 * anything. Deliberately narrow — a network blip while loading a chunk that
 * DOES still exist gives the same message, and reloading is a reasonable answer
 * to that too.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : '';
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) || // Safari
    /ChunkLoadError/i.test(message) ||
    /Loading chunk \S+ failed/i.test(message)
  );
}

/**
 * Reload to pick up the current build, unless one was just attempted. Returns
 * whether a reload was started, so a caller can decide whether to render an
 * error instead.
 */
export function reloadForStaleBuild(): boolean {
  try {
    const previous = Number(sessionStorage.getItem(RELOAD_STAMP) ?? '0');
    if (Number.isFinite(previous) && Date.now() - previous < COOL_OFF_MS) return false;
    sessionStorage.setItem(RELOAD_STAMP, String(Date.now()));
  } catch {
    // Private mode, or storage blocked. Without the guard a loop is possible,
    // so decline rather than risk one — the user sees the error and can refresh
    // by hand, which is the same outcome one step slower.
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Catch the failures that never reach a React error boundary.
 *
 * `vite:preloadError` fires when Vite's own module preload fails — before any
 * component renders, so no boundary is mounted to catch it. The two global
 * handlers cover an import rejected outside React's tree (a prefetch, an event
 * handler). Without these the tab logs to the console and does nothing visible,
 * which is the worst of the available behaviours.
 */
export function installStaleBuildRecovery(): void {
  window.addEventListener('vite:preloadError', () => {
    // Deliberately NOT preventDefault(). Vite's handler is
    // `if (!event.defaultPrevented) throw err`, so cancelling it does not
    // "stop the tab dying" — it makes the import RESOLVE WITH UNDEFINED, and
    // the failure resurfaces further downstream as "Cannot read properties of
    // undefined". Let it throw: the throw is what gives the error boundary a
    // real error to recognise. This listener's only job is the reload.
    reloadForStaleBuild();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isStaleChunkError(event.reason)) {
      event.preventDefault();
      reloadForStaleBuild();
    }
  });

  window.addEventListener('error', (event) => {
    if (isStaleChunkError(event.error ?? event.message)) reloadForStaleBuild();
  });
}
